import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import axios from "axios";
import "css/common.css";
import { useDispatch, useSelector } from "react-redux";
import { Cookies } from "react-cookie";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { openAlert } from "./redux/slices/alertSlice";
import { insertUserInfo } from "./redux/slices/userSlice";
import SignIn from "pages/my/signIn";
import Admin from "pages/admin/admin";
import My from "pages/my/my";
import Alert from "component/common/alert";

function Root() {
  //dispatch
  const dispatch = useDispatch();
  //payload
  const userPayload = useSelector((state) => state.user);
  //cookie
  const cookies = new Cookies();
  //env
  const serverUrl = process.env.REACT_APP_SERVER_URL;
  const authNm = process.env.REACT_APP_AUTH_TOKEN;
  const refreshNm = process.env.REACT_APP_REFRESH_TOKEN;
  const domain = process.env.REACT_APP_DOMAIN;

  //set screen size
  const setScreenSize = useCallback(() => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    let prevVisualViewport = 0;
    const currentVisualViewportHeight = window.visualViewport.height;

    if (
      prevVisualViewport - 30 > currentVisualViewportHeight &&
      prevVisualViewport - 100 < currentVisualViewportHeight
    ) {
      const scrollHeight = window.document.scrollingElement.scrollHeight;
      const scrollTop = scrollHeight - window.visualViewport.height;

      window.scrollTo(0, scrollTop);
    }

    prevVisualViewport = window.visualViewport.height;

    window.visualViewport.onresize = prevVisualViewport;
  }, []);

  const inessentialList = [
    "/user/login",
    "/user/login/admin",
    "/user/status",
    "/license/status",
  ];

  // axios 설정 + interceptor (렌더링 시점에 동기 실행 — 자식 effect보다 먼저)
  const interceptorRef = useRef(false);
  if (!interceptorRef.current) {
    interceptorRef.current = true;

    axios.defaults.url = serverUrl;
    axios.defaults.baseURL = serverUrl;
    axios.defaults.withCredentials = false;

    //axios request
    axios.interceptors.request.use(
      async (config) => {
        if (config.params == null) {
          config.params = {};
        }

        if (!inessentialList.includes(config.url)) {
          const authCookie = cookies.get(authNm);
          const refreshCookie = cookies.get(refreshNm);

          if (authCookie === undefined && refreshCookie === undefined) {
            cookies.remove(authNm, { path: "/", domain: domain });
            cookies.remove(refreshNm, { path: "/", domain: domain });
            window.location.href = process.env.REACT_APP_CLIENT_URL;
          }

          config.headers.Authorization = `Bearer ${authCookie}`;
          config.headers.RefreshAuthorization = `Bearer ${refreshCookie}`;
        }

        const authCookie = cookies.get(authNm);
        const refreshCookie = cookies.get(refreshNm);

        if (authCookie !== undefined && refreshCookie !== undefined) {
          config.headers.Authorization = `Bearer ${authCookie}`;
          config.headers.RefreshAuthorization = `Bearer ${refreshCookie}`;
        }

        return config;
      },
      (err) => {
        return Promise.reject(err);
      },
    );

    //axios response
    axios.interceptors.response.use(
      (res) => {
        if (res.headers.refresh_authorization !== undefined) {
          cookies.remove(authNm, { path: "/", domain: domain });
          cookies.set(authNm, res.headers.refresh_authorization, {
            path: "/",
            domain: domain,
          });
        }

        return res;
      },
      (err) => {
        const response = err.response;

        if (response !== undefined) {
          const status = response.status;

          if (status === 400) {
            cookies.remove(authNm, { path: "/", domain: domain });
            cookies.remove(refreshNm, { path: "/", domain: domain });
          } else if (status === 401) {
            cookies.remove(authNm, { path: "/", domain: domain });
            cookies.remove(refreshNm, { path: "/", domain: domain });

            // 로그인 요청의 401은 signIn.js에서 처리
            const requestUrl = err.config?.url || "";
            if (!requestUrl.includes("/user/login")) {
              dispatch(
                openAlert({
                  title: "알림",
                  content: err.response.data,
                  submitEventHandler: () =>
                    (window.location.href = process.env.REACT_APP_CLIENT_URL),
                }),
              );
            }
          } else if (status === 404) {
            throw new Error("알 수 없는 요청입니다.");
          }
        } else {
          dispatch(
            openAlert({
              title: "오류",
              content: err.code,
              submitEventHandler: () => {},
            }),
          );
        }

        return Promise.reject(err);
      },
    );
  }

  //refresh user info
  const refreshInfo = useCallback(() => {
    axios
      .get("/user/refresh/info")
      .then((res) => {
        if (res.status === 200) {
          const memeberInfo = res.data.MEMBER_INFO;

          dispatch(
            insertUserInfo({
              userNm: memeberInfo.MEMBER_NM,
              userId: memeberInfo.MEMBER_ID,
              email: memeberInfo.EMAIL,
              adminYn: memeberInfo.ADMIN_YN,
              adminType: memeberInfo.TYPE || "",
              campus: memeberInfo.CAMPUS || "",
              campusCd: memeberInfo.CAMPUS_CD || "",
            }),
          );
        }
      })
      .catch(() => {
        const effectCookies = new Cookies();
        effectCookies.remove(authNm, { path: "/" });
        effectCookies.remove(refreshNm, { path: "/" });
        effectCookies.remove(authNm, { path: "/", domain: domain });
        effectCookies.remove(refreshNm, { path: "/", domain: domain });
      });
  }, [authNm, dispatch, domain, refreshNm]);

  //resize eventListener
  useLayoutEffect(() => {
    setScreenSize();
    window.addEventListener("resize", () => setScreenSize());
  });

  const refreshingRef = useRef(false);
  useLayoutEffect(() => {
    if (refreshingRef.current) return;

    const effectCookies = new Cookies();
    const refreshCookie = effectCookies.get(refreshNm);

    if (userPayload.userId === "" && refreshCookie !== undefined) {
      refreshingRef.current = true;
      refreshInfo();
    }
  }, [authNm, refreshInfo, refreshNm, userPayload.userId]);
  const isLoggedIn = userPayload.userId !== "";
  const isAdmin = userPayload.adminYn === "Y";
  const isLoading =
    userPayload.userId === "" && new Cookies().get(refreshNm) !== undefined;

  return (
    <>
      <Alert />
      <BrowserRouter>
        <Routes>
          <Route
            path={process.env.REACT_APP_CLIENT_URL}
            element={<SignIn type="user" />}
          />
          <Route
            path={process.env.REACT_APP_CLIENT_URL + "my/*"}
            element={
              isLoading ? null : isLoggedIn && !isAdmin ? (
                <My />
              ) : isLoggedIn && isAdmin ? (
                <Navigate
                  to={process.env.REACT_APP_CLIENT_URL + "admin"}
                  replace
                />
              ) : (
                <Navigate to={process.env.REACT_APP_CLIENT_URL} replace />
              )
            }
          />

          <Route
            path={process.env.REACT_APP_CLIENT_URL + "admin"}
            element={<Admin />}
          />
          <Route
            path={process.env.REACT_APP_CLIENT_URL + "admin/*"}
            element={
              isLoading ? null : isLoggedIn && isAdmin ? (
                <Admin />
              ) : (
                <Navigate to={process.env.REACT_APP_CLIENT_URL} replace />
              )
            }
          />

          <Route
            path="*"
            element={<Navigate to={process.env.REACT_APP_CLIENT_URL} replace />}
          />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default Root;
